import { PROCESSING_RECEIPT_PAGE } from "./processingReceiptLayout.js"

/**
 * @typedef {{ species: string, process: string, quantity: number }} LineItem
 * @typedef {{
 *   species: number,
 *   process: number,
 *   qty: number,
 * }} LineItemColumnWidths
 * @typedef {{
 *   speciesX: number,
 *   processX: number,
 *   qtyX: number,
 *   fontSize: number,
 *   columnGap: number,
 *   columnWidths: { species: number, process: number, qty: number },
 *   contentWidths: LineItemColumnWidths,
 * }} LineItemColumnLayout
 */

/**
 * Measure the widest rendered string in each line-item column.
 *
 * @param {LineItem[]} lines
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @returns {LineItemColumnWidths}
 */
export function measureLineItemColumnWidths(lines, font, fontSize) {
    let species = 0
    let process = 0
    let qty = 0

    for (const line of lines ?? []) {
        species = Math.max(
            species,
            font.widthOfTextAtSize(String(line.species ?? ""), fontSize)
        )
        process = Math.max(
            process,
            font.widthOfTextAtSize(String(line.process ?? ""), fontSize)
        )
        qty = Math.max(
            qty,
            font.widthOfTextAtSize(`x ${line.quantity}`, fontSize)
        )
    }

    return { species, process, qty }
}

/**
 * Derive minimum column widths from the original fixed x anchors.
 *
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @param {{ speciesX?: number, processX?: number, qtyRightX?: number }} originalColumns
 */
export function minimumLineItemColumnWidths(font, fontSize, originalColumns = {}) {
    const speciesX = originalColumns.speciesX ?? 16
    const processX = originalColumns.processX ?? 65
    const qtyRightX = originalColumns.qtyRightX ?? 168

    const minQtyWidth = font.widthOfTextAtSize("x 99", fontSize)

    return {
        species: processX - speciesX,
        process: Math.max(0, qtyRightX - processX - minQtyWidth),
        qty: minQtyWidth,
    }
}

/**
 * Compute x positions for species, process, and qty columns laid out left-to-right.
 *
 * Each column is at least as wide as the original template spacing; wider content
 * expands columns and pushes later columns right. Qty follows process (not
 * page-right anchored).
 *
 * @param {LineItem[]} lines
 * @param {import('pdf-lib').PDFFont} font
 * @param {object} [itemsConfig]
 * @param {number} [pageWidth]
 * @returns {LineItemColumnLayout}
 */
export function computeLineItemColumnLayout(
    lines,
    font,
    itemsConfig = {},
    pageWidth = PROCESSING_RECEIPT_PAGE.width
) {
    const fontSize = itemsConfig.size ?? 9
    const columnGap = itemsConfig.columnGap ?? 6
    const rightMargin = itemsConfig.rightMargin ?? 13
    const maxContentRight = pageWidth - rightMargin

    const originalColumns = {
        speciesX: itemsConfig.originalSpeciesX ?? itemsConfig.speciesX ?? 16,
        processX: itemsConfig.originalProcessX ?? itemsConfig.processX ?? 65,
        qtyRightX: itemsConfig.originalQtyRightX ?? itemsConfig.qtyRightX ?? 168,
    }

    const contentWidths = measureLineItemColumnWidths(lines, font, fontSize)
    const minWidths = minimumLineItemColumnWidths(font, fontSize, originalColumns)

    let speciesColumnWidth = Math.max(contentWidths.species, minWidths.species)
    let processColumnWidth = Math.max(contentWidths.process, minWidths.process)
    let qtyColumnWidth = Math.max(contentWidths.qty, minWidths.qty)

    const speciesX = originalColumns.speciesX
    let processX = speciesX + speciesColumnWidth + columnGap
    let qtyX = processX + processColumnWidth + columnGap

    if (qtyX + qtyColumnWidth > maxContentRight) {
        const available = maxContentRight - speciesX
        const gapsWidth = columnGap * 2
        const totalColumnWidth = speciesColumnWidth + processColumnWidth + qtyColumnWidth

        if (totalColumnWidth + gapsWidth > available) {
            const scale = Math.max(0, (available - gapsWidth) / totalColumnWidth)
            speciesColumnWidth *= scale
            processColumnWidth *= scale
            qtyColumnWidth *= scale
        }

        processX = speciesX + speciesColumnWidth + columnGap
        qtyX = processX + processColumnWidth + columnGap

        if (qtyX + qtyColumnWidth > maxContentRight) {
            qtyX = Math.max(processX + columnGap, maxContentRight - qtyColumnWidth)
            processColumnWidth = Math.max(
                minWidths.process,
                qtyX - columnGap - processX
            )
            qtyX = processX + processColumnWidth + columnGap
        }
    }

    return {
        speciesX,
        processX,
        qtyX,
        fontSize,
        columnGap,
        columnWidths: {
            species: speciesColumnWidth,
            process: processColumnWidth,
            qty: qtyColumnWidth,
        },
        contentWidths,
    }
}

/**
 * @param {string} text
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} size
 * @param {number} maxWidth
 */
export function truncateTextToWidth(text, font, size, maxWidth) {
    const value = String(text ?? "")
    if (!value || font.widthOfTextAtSize(value, size) <= maxWidth) {
        return value
    }

    const ellipsis = "..."
    let result = ""
    for (const ch of value) {
        const next = result + ch
        if (font.widthOfTextAtSize(next + ellipsis, size) > maxWidth) {
            break
        }
        result = next
    }

    return result ? `${result}${ellipsis}` : ellipsis
}
